import type { CapiProductPage } from 'types/capi/pdp';

import { Button } from '~/components/Button';
import ProductNotificationButton from '~/components/ProductNotificationButton';
import useIsPendingConfirmation from '~/hooks/useIsPendingConfirmation';
import usePageData from '~/hooks/usePageData';
import useRegion from '~/hooks/useRegion';
import useUser from '~/hooks/useUser';

import AnimatedFrame from './AnimatedFrame';

const UpcomingReleaseText = () => {
  return (
    <p className="w-full">
      This product is{' '}
      <span className="text-purple-hola-light">coming soon</span>, set a release
      notification.
    </p>
  );
};

const SoldOutText = () => {
  return (
    <p className="w-full">
      This product is <span className="text-red-alert-light">sold out</span>,
      set a restock notification.
    </p>
  );
};

const ConfirmEmailText = () => {
  return (
    <p className="w-full">
      Notification <span className="text-golden-syrup">set up incomplete</span>.
      Confirm your email address.
    </p>
  );
};

type BannerType = 'sold-out' | 'upcoming';

type Props = {
  bannerType: BannerType;
  onDismiss: () => void;
};

const NotificationBanner = ({ bannerType, onDismiss }: Props) => {
  const product = usePageData<CapiProductPage>();
  const region = useRegion();
  const isNotified = useUser().isNotificationSetForProduct(product.legacy_id);
  const isPendingConfirmation = useIsPendingConfirmation(
    product.product_id,
    isNotified
  );

  if (region.code !== 'GB') return null;

  return (
    <AnimatedFrame>
      <div className="content-width flex w-full flex-col items-center justify-between gap-2 py-4 md:flex-row">
        {isPendingConfirmation ? (
          <ConfirmEmailText />
        ) : bannerType === 'upcoming' ? (
          <UpcomingReleaseText />
        ) : (
          <SoldOutText />
        )}
        <div className="flex w-full gap-2">
          <Button
            onClick={onDismiss}
            className="w-1/3"
            variant="transparent-white-border"
          >
            Dismiss
          </Button>
          <div className="w-2/3">
            <ProductNotificationButton
              product={{
                id: product.product_id,
                image: product.gallery[0],
                legacyId: product.legacy_id,
                name: product.name,
                price: product.price.regular
              }}
              className="min-h-12"
              pagePosition="Banner"
              mode="dark"
              buttonStyle="normal"
            />
          </div>
        </div>
      </div>
    </AnimatedFrame>
  );
};

export default NotificationBanner;
